import { sendNotification, useApiClient, useEntityMetadata, useTranslation } from '@msdk/common'
import { Entity, EntityModel, OptionsetValue } from '@msdk/definitions'
import { distinctBy } from '@msdk/forms'
import {
  ButtonBase,
  COLORS,
  createComponentWrapperWithRef,
  IconSelector,
  InfiniteScroll,
  Spinner,
  styled,
  Typography,
  uiTokens,
} from '@msdk/ui'
import { useEffect, useState } from 'react'

import { NotificationCard } from './NotificationCard'

const MuiStyledContainer = styled('div')`
  display: flex;
  flex-direction: column;
  width: 448px;
  max-height: 430px;
`

const NovaStyledContainer = styled('div')`
  display: flex;
  flex-direction: column;
  width: 448px;
  max-height: 600px;
  overflow: hidden;
  border-radius: ${uiTokens.radius.xLarge}px;
  border: ${uiTokens.sizing.stroke.light}px solid ${uiTokens.uiStroke.neutral};
  background: ${uiTokens.uiSurface.interactive};
`

export const StyledContainer = createComponentWrapperWithRef(
  {
    mui: MuiStyledContainer,
    nova: NovaStyledContainer,
  },
  'StyledContainer',
)

const MuiHeader = styled('div')`
  display: flex;
  flex-direction: row;
  padding-left: 24px;
  padding-right: 24px;
  min-height: 56px;
  align-items: center;
`

const NovaHeader = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: ${uiTokens.sizing.uiControl.height.large}px;
  padding: ${uiTokens.spacing.padding.semiMedium}px ${uiTokens.spacing.padding.semiMedium}px
    ${uiTokens.spacing.padding.small}px ${uiTokens.spacing.padding.medium}px;
  gap: ${uiTokens.spacing.gap.small}px;
  align-self: stretch;
  color: ${uiTokens.uiContent.emphasized};
`

export const Header = createComponentWrapperWithRef(
  {
    mui: MuiHeader,
    nova: NovaHeader,
  },
  'Header',
)

const BodyContainer = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  min-height: 0;
  align-self: stretch;
  border-top: ${uiTokens.sizing.stroke.light}px solid ${uiTokens.uiStroke.neutral};
  border-bottom: ${uiTokens.sizing.stroke.light}px solid ${uiTokens.uiStroke.neutral};
`

export const StyledBodyContainer = createComponentWrapperWithRef(
  {
    mui: BodyContainer,
    nova: styled(BodyContainer)`
      padding: ${uiTokens.spacing.padding.medium}px 0;
    `,
  },
  'StyledBodyContainer',
)

const NovaFooter = styled('div')`
  display: flex;
  padding: ${uiTokens.spacing.padding.medium}px;
  justify-content: flex-end;
  align-items: center;
  gap: ${uiTokens.spacing.gap.medium}px;
  align-self: stretch;
`

export const Footer = createComponentWrapperWithRef(
  {
    mui: MuiHeader,
    nova: NovaFooter,
  },
  'Footer',
)

const MuiHeaderText = styled('h1')`
  font-size: 20px;
  line-height: 24px;
  font-weight: 700;
`

const NovaHeaderText = styled('h1')`
  overflow: hidden;
  color: ${uiTokens.uiContent.emphasized};
  text-overflow: ellipsis;
  font-size: ${uiTokens.font.fontSize.title.large}px;
  font-style: normal;
  font-weight: ${uiTokens.font.fontWeight.semiBold};
  line-height: ${uiTokens.font.lineHeight.title.large}px;
  letter-spacing: ${uiTokens.font.letterSpacing.normal};
`

export const HeaderText = createComponentWrapperWithRef(
  {
    mui: MuiHeaderText,
    nova: NovaHeaderText,
  },
  'HeaderText',
)

const MuiMarkAllAsReadButton = styled(ButtonBase)`
  margin-left: auto;
  margin-top: 5px;
  font-size: 15px;
  line-height: 24px;
  font-weight: 700;
  border: 0;
  padding: 5px;
  color: ${COLORS.button.main};
  &:hover {
    background-color: unset;
    border: none;
  }

  &.Mui-disabled {
    color: rgba(0, 0, 0, 0.33);
    border: none;
  }
`
const NovaMarkAllAsReadButton = styled(ButtonBase)`
  display: flex;
  height: ${uiTokens.sizing.uiControl.height.medium}px;
  padding: 0 ${uiTokens.spacing.padding.medium}px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: ${uiTokens.uiContent.accent};
  background-color: transparent;
  border-radius: ${uiTokens.radius.round}px;
  font-size: ${uiTokens.font.fontSize.uiControlText.medium}px;
  font-style: normal;
  font-weight: ${uiTokens.font.fontWeight.semiBold};
  line-height: ${uiTokens.font.lineHeight.uiControlText.medium}px;
  letter-spacing: ${uiTokens.font.letterSpacing.normal};
  border: 0;
  box-shadow: none;
  &:hover {
    background-color: unset;
  }

  &.Mui-disabled {
    color: ${uiTokens.uiContent.disabled};
    background-color: unset;
  }
`

export const MarkAllAsReadButton = createComponentWrapperWithRef(
  {
    mui: MuiMarkAllAsReadButton,
    nova: NovaMarkAllAsReadButton,
  },
  'MarkAllAsReadButton',
)

export const MarkAllAsReadIcon = createComponentWrapperWithRef(
  {
    mui: styled(IconSelector)`
      margin-right: 8px;
    `,
    nova: styled(IconSelector)`
      display: none;
    `,
  },
  'MarkAllAsReadIcon',
)

const ContentContainer = styled('div')`
  display: flex;
  flex-direction: column;
`

const LoaderContainer = styled('div')`
  padding-top: 16px;
  padding-bottom: 16px;
  margin-left: auto;
  margin-right: auto;
  display: flex;
  flex-direction: column;
  height: 104px;
`

export const StyledLoaderContainer = createComponentWrapperWithRef(
  {
    mui: LoaderContainer,
    nova: styled(LoaderContainer)`
      margin: 0 10px 0 ${uiTokens.spacing.padding.medium}px;
      border-top: 0.5px solid ${uiTokens.uiStroke.neutral};
      color: ${uiTokens.uiContent.neutral};
      padding: 30px 0;
    `,
  },
  'StyledLoaderContainer',
)

const FirstLoadContainer = styled('div')`
  margin: auto;
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 104px;
`

const MuiLoader = styled(Spinner)`
  display: flex;
  min-height: 40px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  color: rgba(0, 0, 0, 0.8);
`

const NovaLoader = styled(Spinner)`
  display: flex;
  width: 32px;
  height: ${uiTokens.sizing.uiControl.height.small}px;
  justify-content: center;
  align-items: center;
  gap: ${uiTokens.spacing.gap.medium}px;
  aspect-ratio: 1/1;
  color: ${uiTokens.uiStroke.neutralIndicator};
  align-self: center;
`

export const Loader = createComponentWrapperWithRef(
  {
    mui: MuiLoader,
    nova: NovaLoader,
  },
  'Loader',
)

const EmptyMessageContainer = styled('div')`
  margin: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 104px;
`

const MuiEmptyMessageText = styled('p')`
  margin: 0;
  color: rgba(0, 0, 0, 0.6);
`

const NovaEmptyMessageText = styled('p')`
  margin: 0;
  color: ${uiTokens.uiContent.neutral};
`

export const EmptyMessageText = createComponentWrapperWithRef(
  {
    mui: MuiEmptyMessageText,
    nova: NovaEmptyMessageText,
  },
  'EmptyMessageText',
)

const NoMoreNotificationsContainer = styled('div')`
  display: flex;
  min-height: 40px;
  justify-content: center;
  align-items: center;
  gap: 10px;
`

export const StyledNoMoreNotificationsContainer = createComponentWrapperWithRef(
  {
    mui: styled(NoMoreNotificationsContainer)`
      color: rgba(0, 0, 0, 0.6);
    `,
    nova: styled(NoMoreNotificationsContainer)`
      margin: 0 10px 0 ${uiTokens.spacing.padding.medium}px;
      border-top: 0.5px solid ${uiTokens.uiStroke.neutral};
      color: ${uiTokens.uiContent.neutral};
      padding: 40px 0;
    `,
  },
  'StyledNoMoreNotificationsContainer',
)

type MarkAllResponse = { result: boolean }

interface Props {
  onClose: () => void
  onNotificationUpdated: (refresh?: boolean) => void
  unreadNotificationCount: number
  unreadNotificationCountLoading: boolean
}

export function NotificationPopup(props: Props) {
  const api = useApiClient()
  const model = useEntityMetadata('NOTIFICATION') as EntityModel
  const t = useTranslation()
  const pageSize = 5

  const [notificatons, setNotifications] = useState<Entity[]>([])
  const [page, setPage] = useState(0)
  const [hasNextPage, setHasNextPage] = useState(false)
  const [firstLoad, setFirstLoad] = useState(true)

  // newPage parameter is here to cover cases where we are calling
  // fetchData just after updateting the page state, in these cases
  // the state could not be updated in time when we enter in the fetch
  // so we use an optional parameter that will take priority over the state
  const fetchData = async (newPage?: number) => {
    const res = await api.entity.getView('CMPS', model, 'NOTIFICATION_VIEW', {
      pageNumber: newPage ?? page,
      pageSize,
    })
    setNotifications((prevItems) => distinctBy([...prevItems, ...res.source], 'id'))
    setPage((newPage ?? page) + 1)
    setHasNextPage(res.hasNextPage)
  }

  const markAsRead = async () => {
    setFirstLoad(true)
    const res = (await api.read<MarkAllResponse>('CMPS/api/notification/markallasread')) as MarkAllResponse
    if (res.result) {
      setPage(0)
      setNotifications([])
      await fetchData(0)
      props.onNotificationUpdated(true)
    } else {
      console.error('[NotificationPopup] - error mark all as read')
      sendNotification({
        message: t('CMPS_NOTIFICATION_MARK_AS_READ_ERROR', 'Error while marking all notification as read.'),
        status: 'error',
      })
    }
    setFirstLoad(false)
  }

  const onNotificationUpdate = async (id: string) => {
    const toUpdate = notificatons.find((n) => n.id === id) as Entity
    const opt = toUpdate.fields['NOTIFICATION_STATUS'] as OptionsetValue
    opt.value = 'READ'
    setNotifications([...notificatons])
    props.onNotificationUpdated()
  }

  useEffect(() => {
    const fetch = async () => {
      await fetchData()
      setFirstLoad(false)
    }
    fetch().catch(console.error)
  }, [])

  return (
    <StyledContainer>
      <Header>
        <HeaderText>{t('LBL_NOTIFICATIONS', 'Notifications')}</HeaderText>
      </Header>
      <StyledBodyContainer>
        {firstLoad && (
          <FirstLoadContainer>
            <Loader size={32} />
          </FirstLoadContainer>
        )}
        {!firstLoad && (
          <InfiniteScroll
            next={() => fetchData()}
            hasMore={hasNextPage}
            dataLength={notificatons.length}
            preventXOverflow={true}
            loader={
              <StyledLoaderContainer>
                <Loader size={32} />
              </StyledLoaderContainer>
            }
          >
            {notificatons.length > 0 && (
              <>
                <ContentContainer>
                  {notificatons.map((n, index, array) => {
                    const isLast = index === array.length - 1
                    return (
                      <NotificationCard
                        notification={n}
                        key={n.id}
                        hideDivider={isLast}
                        onClose={props.onClose}
                        onNotificationUpdated={onNotificationUpdate}
                      />
                    )
                  })}
                </ContentContainer>
                {!hasNextPage && notificatons.length >= pageSize && (
                  <StyledNoMoreNotificationsContainer>
                    <Typography variant="body2">{t('LBL_NOTIFICATION_END', 'No more notifications')}</Typography>
                  </StyledNoMoreNotificationsContainer>
                )}
              </>
            )}
            {notificatons.length === 0 && (
              <EmptyMessageContainer>
                <EmptyMessageText>
                  {t('LBL_NOTIFICATION_EMPTY_LIST_NEW_1', 'There are no notifications')}
                </EmptyMessageText>
                <EmptyMessageText>{t('LBL_NOTIFICATION_EMPTY_LIST_NEW_2', 'at the moment.')}</EmptyMessageText>
              </EmptyMessageContainer>
            )}
          </InfiniteScroll>
        )}
      </StyledBodyContainer>
      <Footer>
        <MarkAllAsReadButton
          disabled={
            firstLoad ||
            props.unreadNotificationCount === 0 ||
            props.unreadNotificationCountLoading ||
            notificatons.length === 0
          }
          onClick={markAsRead}
        >
          <MarkAllAsReadIcon iconName="DraftsOutlined" />
          {t('CMPS_MARK_ALL_AS_READ_LABEL', 'Mark all as read')}
        </MarkAllAsReadButton>
      </Footer>
    </StyledContainer>
  )
}
